import { Card, CardContent, CardHeader, Menu, MenuItem, useMediaQuery } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import GenericButton from "components/Buttons/GenericButton";
import FormDatePickerV2 from "components/FormFields/FormDatePickerV2";
import FormDropdown from "components/FormFields/FormDropdown";
import FormInputV2 from "components/FormFields/FormInputV2";
import { getMyWorkCenters } from "crud/workCentersEmployer.crud";
import { useDataset } from "hooks/useDataset";
import useTokenHelper from "hooks/useTokenHelper";
import { DATASET, REPORT_FILE_NAME } from "utils/const";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const FilterSection = ({
  FromRENT = false,
  submissionTypeId = null,
  sortColumn,
  sortDirection,
  isLoading = false,
  exportDisabled = false,
  onExport,
  onSearch,
  onClear,
  onAutoSearch,
}) => {
  const classes = useStylesGeneric();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const tokenHelper = useTokenHelper();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    trigger,
    watch,
  } = useFormContext();

  const { data: professionalCategory } = useDataset({ name: DATASET.EMPLOYMENT_PROFESSIONAL_CATEGORY });
  const { data: genders } = useDataset({ name: DATASET.GENDER });
  const { data: nationalities } = useDataset({ name: DATASET.NATIONALITIES });
  const { data: employmentStatus } = useDataset({ name: DATASET.EMPLOYMENT_STATUS });

  const { data: workCenters } = useQuery(["my-workcenters"], () => getMyWorkCenters(), {
    enabled: !!tokenHelper?.getUserId(),
    select: (data) => {
      if (!data) return [];

      return data.map((item) => ({
        code: item.id,
        label: item.value,
      }));
    },
  });

  const onSubmit = async () => {
    if (FromRENT) {
      await queryClient.invalidateQueries(["listWorkCenterEmployers"]);
    } else {
      await onSearch?.({ FromRENT, submissionTypeId, sortColumn, sortDirection });
    }
  };

  const handleClearFilters = async () => {
    if (FromRENT) {
      await queryClient.invalidateQueries(["listWorkCenterEmployers"]);
    } else {
      await onClear?.({ FromRENT, submissionTypeId, sortColumn, sortDirection });
    }
  };

  const showInactive = watch("showInactive");
  const expiringIn30Days = watch("expiringIn30Days");
  const SalaryAlertOn = watch("SalaryAlertOn");
  const didMountRef = useRef(false);

  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchorEl);

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  useEffect(() => {
    // Ao mudar qualquer switch, refaz a pesquisa e persiste na URL.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (FromRENT) {
      queryClient.invalidateQueries(["listWorkCenterEmployers"]);
    } else {
      onAutoSearch?.({ FromRENT, submissionTypeId, sortColumn, sortDirection });
    }
  }, [showInactive, expiringIn30Days, SalaryAlertOn]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card classes={{ root: classes.cardRoot }}>
      <form id="filter-employees" onSubmit={handleSubmit(onSubmit)}>
        <CardHeader
          classes={{
            action: classes.cardHeaderAction,
            title: classes.cardHeader,
          }}
          title={
            <Box>Pesquisa de Trabalhadores</Box>
          }
        />
        <CardContent>
          <Box>
            <Grid container classes={{ root: classes.containerPadding }}>
              <Grid item xs={12} sm={6} md={4}>
                <FormInputV2
                  name="name"
                  label="Nome do Trabalhador"
                  placeholder="Informe o nome do trabalhador"
                  control={control}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormInputV2 name="ssn" label="NISS" placeholder="Informe o NISS" control={control} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormInputV2 name="nif" label="NIF" placeholder="Informe o NIF" control={control} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormInputV2
                  name="documentNumber"
                  label="N.º Documento de Identificação"
                  placeholder="Informe o N.º Documento de Identificação"
                  control={control}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormDatePickerV2
                  name="startDate"
                  label="Data de Registo (Desde)"
                  control={control}
                  maxDateToday={true}
                  minDateToday={false}
                  endDateFieldName="endDate"
                  errors={errors}
                  classes={classes}
                  trigger={trigger}
                  getValues={getValues}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormDatePickerV2
                  name="endDate"
                  label="Data de Registo (Até)"
                  control={control}
                  maxDateToday={true}
                  minDateToday={false}
                  startDateFieldName="startDate"
                  errors={errors}
                  classes={classes}
                  trigger={trigger}
                  getValues={getValues}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormDropdown
                  name="workCenterId"
                  label="Centro de Trabalho"
                  mappingTo="code"
                  control={control}
                  options={workCenters}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormDropdown
                  name="employeeStatusId"
                  label="Situação Profissional"
                  mappingTo="code"
                  control={control}
                  options={employmentStatus}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormDropdown
                  name="professionalCategoryId"
                  label="Categoria Profissional"
                  mappingTo="code"
                  control={control}
                  options={professionalCategory}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormDropdown
                  name="genderId"
                  label="Género"
                  mappingTo="code"
                  control={control}
                  options={genders}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormDropdown
                  name="nationalityId"
                  label="Nacionalidade"
                  mappingTo="code"
                  control={control}
                  options={nationalities}
                />
              </Grid>
              {/* Botões de Pesquisa */}
              <Grid item xs={12} md={12} classes={{ root: classes.textAlignRight }}>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: isSmDown ? "stretch" : "flex-end",
                    columnGap: 12,
                    rowGap: 12,
                    flexWrap: isSmDown ? "nowrap" : "wrap",
                    flexDirection: isSmDown ? "column" : "row",
                  }}
                >
                  <GenericButton
                    type="button"
                    color="ghost"
                    onClick={handleClearFilters}
                    fullWidth={isSmDown}
                    size={isSmDown ? "medium" : undefined}
                  >
                    Limpar Filtros
                  </GenericButton>
                  <span style={isSmDown ? { width: "100%" } : undefined}>
                    <GenericButton
                      type="button"
                      color="primary"
                      onClick={handleOpenExportMenu}
                      disabled={!!exportDisabled}
                      fullWidth={isSmDown}
                      size={isSmDown ? "medium" : undefined}
                    >
                      Exportar <KeyboardArrowDownIcon />
                    </GenericButton>
                    <Menu
                      anchorEl={exportMenuAnchorEl}
                      open={exportMenuOpen}
                      onClose={handleCloseExportMenu}
                      keepMounted
                    >
                      <MenuItem
                        onClick={() => {
                          handleCloseExportMenu();
                          onExport?.(REPORT_FILE_NAME.PDF_EXTENSION);
                        }}
                      >
                        PDF
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleCloseExportMenu();
                          onExport?.(REPORT_FILE_NAME.EXCEL_EXTENSION);
                        }}
                      >
                        Excel
                      </MenuItem>
                    </Menu>
                  </span>
                  <GenericButton
                    form="filter-employees"
                    onClick={handleSubmit(onSubmit)}
                    color="primary"
                    loading={isLoading}
                    fullWidth={isSmDown}
                    size={isSmDown ? "medium" : undefined}
                  >
                    Pesquisar
                  </GenericButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </form>
    </Card>
  );
};

export default FilterSection;
